/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Loading from '../../../../components/Loading';
import useGoogleMaps from '../../../../hooks/useGoogleMaps';
import { readableInk } from '../derivations';

// Quadradinho de CLASSE DE CARGA (cargo_class, handoff 0032) na borda inferior
// do pino. É rótulo MANUAL do operador — puramente informativo, sem cálculo nem
// fallback derivado. Cores por classe A/B/C/D; SEM classe → cinza com "N/A"
// (feedback pro operador classificar).
const CARGO_FILL = {
   A: '#FFFFFF',
   B: '#FCD34D',
   C: '#F59E0B',
   D: '#B91C1C',
};
const CARGO_NA_FILL = '#9CA3AF';

// Estilo do quadradinho dado o cargo_class (nullable). Sem classe → cinza/"N/A"
// com fonte menor pra caber as 3 letras no mesmo quadrado.
function cargoBadge(cargoClass) {
   if (!cargoClass) {
      return { fill: CARGO_NA_FILL, ink: '#1F1F18', text: 'N/A', fontSize: 6 };
   }
   return {
      fill: CARGO_FILL[cargoClass] || '#FFFFFF',
      ink: cargoClass === 'D' ? '#FFFFFF' : '#1F1F18',
      text: cargoClass,
      fontSize: 8,
   };
}

// Centro de fallback (Goiânia) quando não há nenhum ponto nem base com coordenada.
const FALLBACK_CENTER = { lat: -16.6869, lng: -49.2648 };

// Esconde os POIs nativos do Google (lojas, restaurantes, ícones de negócios) —
// ruído puro num mapa operacional. Mantém a geometria de parques/áreas verdes
// (só tira os rótulos) e as vias/trânsito, que ajudam a se localizar.
const HIDE_POI_STYLES = [
   { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
   { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

// Zoom por scroll mais suave. O passo nativo do Google é 1 nível inteiro por
// "notch" do mouse (~deltaY 100), brusco demais pro operador. Aqui o passo é
// proporcional ao deltaY e limitado, com zoom fracionário ligado e ancorado no
// ponto sob o cursor (mesma sensação do zoom nativo, só que gradual).
const ZOOM_SENSITIVITY = 0.002; // deltaY 100 → ~0.2 nível por notch
const MAX_ZOOM_STEP = 0.5; // teto por evento (trackpads disparam muitos)
const MIN_ZOOM = 3;
const MAX_ZOOM = 20;

function HFPin({
   x,
   y,
   n,
   c,
   done = false,
   run = false,
   started = false,
   selected = false,
   cargo = null,
   onClick = null,
}) {
   const r = 13;
   const sq = 12;
   const sqY = y + r;
   const badge = cargoBadge(cargo);

   const handleClick = onClick
      ? (e) => {
           e.stopPropagation();
           onClick();
        }
      : undefined;

   return (
      <g
         filter="url(#hf-pin-shadow)"
         onClick={handleClick}
         style={onClick ? { cursor: 'pointer' } : undefined}
      >
         {selected && (
            <>
               <circle
                  cx={x}
                  cy={y}
                  r={r + 8}
                  fill="none"
                  stroke={c}
                  strokeWidth="2"
                  opacity=".25"
               />
               <circle
                  cx={x}
                  cy={y}
                  r={r + 4}
                  fill="none"
                  stroke={c}
                  strokeWidth="2"
                  opacity=".55"
               />
            </>
         )}
         {run && (
            <circle
               cx={x}
               cy={y}
               r={r + 4}
               fill="none"
               stroke={c}
               strokeWidth="2"
               opacity=".5"
            >
               <animate
                  attributeName="r"
                  from={r + 2}
                  to={r + 8}
                  dur="1.5s"
                  repeatCount="indefinite"
               />
               <animate
                  attributeName="opacity"
                  from=".6"
                  to="0"
                  dur="1.5s"
                  repeatCount="indefinite"
               />
            </circle>
         )}
         <circle
            cx={x}
            cy={y}
            r={r}
            fill={started ? c : 'white'}
            stroke={c}
            strokeWidth="2.5"
            opacity={done ? 0.55 : 1}
         />
         <text
            x={x}
            y={y + 3.5}
            textAnchor="middle"
            fontSize="10"
            fontWeight="700"
            fill={started ? 'white' : c}
            fontFamily="JetBrains Mono"
         >
            {n}
         </text>
         {/* Frame de classe de carga (cargo_class) */}
         <rect
            x={x - sq / 2}
            y={sqY - sq / 2}
            width={sq}
            height={sq}
            rx="2"
            fill={badge.fill}
            stroke={c}
            strokeWidth="1.5"
         />
         <text
            x={x}
            y={sqY + 3}
            textAnchor="middle"
            fontSize={badge.fontSize}
            fontWeight="800"
            fill={badge.ink}
            fontFamily="JetBrains Mono"
         >
            {badge.text}
         </text>
      </g>
   );
}

HFPin.propTypes = {
   x: PropTypes.number.isRequired,
   y: PropTypes.number.isRequired,
   n: PropTypes.number.isRequired,
   c: PropTypes.string.isRequired,
   done: PropTypes.bool,
   run: PropTypes.bool,
   started: PropTypes.bool,
   selected: PropTypes.bool,
   cargo: PropTypes.string,
   onClick: PropTypes.func,
};

// ===========================================================================
// HFMapSVG — mapa esquemático (fallback). Render original: usado quando não há
// chave de Maps configurada, a API falha ao carregar, ou no protótipo (?mock).
// ===========================================================================
function HFMapSVG({
   dark = false,
   routes,
   unroutedPins,
   onPinClick = null,
   selected = null,
}) {
   const W = 1080;
   const H = 720;
   const tileBg = dark ? '#0E0E0C' : '#EFEEE8';
   const water = dark ? '#152633' : '#D6E4EC';
   const park = dark ? '#11221A' : '#DDE7D8';
   const blockBg = dark ? '#161410' : '#F5F4EE';
   const roadHi = dark ? '#3A3833' : '#FFFFFF';
   const road = dark ? '#262420' : '#F8F7F1';
   const roadEdge = dark ? '#1F1D1A' : '#E2E0D6';

   return (
      <svg
         className="hf-map"
         viewBox={`0 0 ${W} ${H}`}
         preserveAspectRatio="xMidYMid slice"
         style={{ width: '100%', height: '100%' }}
      >
         <defs>
            <pattern
               id="hf-grid"
               x="0"
               y="0"
               width="60"
               height="60"
               patternUnits="userSpaceOnUse"
            >
               <rect width="60" height="60" fill={blockBg} />
               <line
                  x1="0"
                  y1="0"
                  x2="60"
                  y2="0"
                  stroke={roadEdge}
                  strokeWidth=".5"
               />
               <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="60"
                  stroke={roadEdge}
                  strokeWidth=".5"
               />
            </pattern>
            <filter
               id="hf-pin-shadow"
               x="-50%"
               y="-50%"
               width="200%"
               height="200%"
            >
               <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity=".25" />
            </filter>
         </defs>

         <rect width={W} height={H} fill={tileBg} />
         <rect width={W} height={H} fill="url(#hf-grid)" opacity=".6" />

         {/* Rio + costa */}
         <path
            d="M -20 80 Q 200 140 380 100 Q 540 70 700 180 Q 840 280 1100 240 L 1100 -20 L -20 -20 Z"
            fill={water}
            opacity=".55"
         />
         <path
            d="M -20 480 Q 180 500 360 540 Q 560 580 760 540 Q 920 510 1100 560 L 1100 740 L -20 740 Z"
            fill={water}
            opacity=".25"
         />

         {/* Parques */}
         <path
            d="M 120 280 Q 180 260 240 290 Q 280 310 260 360 Q 220 390 160 380 Q 100 360 120 280 Z"
            fill={park}
            opacity=".75"
         />
         <path
            d="M 760 380 Q 820 360 880 390 Q 920 430 880 480 Q 820 510 770 490 Q 720 460 760 380 Z"
            fill={park}
            opacity=".75"
         />

         {/* Vias principais */}
         <g stroke={roadHi} fill="none" strokeLinecap="round">
            <path
               d="M 0 360 Q 240 340 480 360 Q 720 380 1080 350"
               strokeWidth="14"
            />
            <path
               d="M 540 0 Q 530 200 560 380 Q 580 540 540 720"
               strokeWidth="14"
            />
         </g>
         <g stroke={road} fill="none" strokeLinecap="round" opacity=".9">
            <path
               d="M 0 220 Q 300 200 540 220 Q 780 240 1080 210"
               strokeWidth="6"
            />
            <path
               d="M 0 540 Q 280 520 520 540 Q 760 560 1080 540"
               strokeWidth="6"
            />
            <path
               d="M 280 0 Q 270 200 290 380 Q 310 540 280 720"
               strokeWidth="6"
            />
            <path
               d="M 800 0 Q 790 200 810 380 Q 830 540 800 720"
               strokeWidth="6"
            />
         </g>

         {/* Linhas das rotas com glow externo (rotas de 1 pino não têm linha) */}
         {routes.map(
            (r) =>
               r.d && (
                  <g key={r.id}>
                     <path
                        d={r.d}
                        fill="none"
                        stroke={r.color}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity=".9"
                     />
                     <path
                        d={r.d}
                        fill="none"
                        stroke={r.color}
                        strokeWidth="9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity=".15"
                     />
                  </g>
               )
         )}

         {/* Pinos das rotas — clicáveis quando a rota tiver subclusterId */}
         {routes.map((r) =>
            r.pins.map((p, i) => {
               const isSelected =
                  selected &&
                  selected.subclusterId === r.subclusterId &&
                  selected.seq === p.n;
               return (
                  <HFPin
                     key={`${r.id}-${i}`}
                     x={p.x}
                     y={p.y}
                     n={p.n}
                     c={r.color}
                     done={p.done}
                     run={p.run}
                     started={p.started ?? (p.done || p.run)}
                     cargo={p.cargo}
                     selected={isSelected}
                     onClick={
                        onPinClick && r.subclusterId
                           ? () => onPinClick(r.subclusterId, p.n)
                           : undefined
                     }
                  />
               );
            })
         )}

         {/* Não-roteirizados (cinza tracejado) */}
         {unroutedPins.map((p, i) => (
            <g key={`u-${i}`} filter="url(#hf-pin-shadow)">
               <circle
                  cx={p.x}
                  cy={p.y}
                  r="9"
                  fill={dark ? '#1A1A17' : 'white'}
                  stroke={dark ? '#7A7A72' : '#8B8B82'}
                  strokeWidth="1.5"
                  strokeDasharray="2 2"
               />
               <circle
                  cx={p.x}
                  cy={p.y}
                  r="2.5"
                  fill={dark ? '#7A7A72' : '#8B8B82'}
               />
            </g>
         ))}
      </svg>
   );
}

HFMapSVG.propTypes = {
   dark: PropTypes.bool,
   routes: PropTypes.array.isRequired,
   unroutedPins: PropTypes.array.isRequired,
   onPinClick: PropTypes.func,
   selected: PropTypes.shape({
      subclusterId: PropTypes.string,
      seq: PropTypes.number,
   }),
};

// ===========================================================================
// HFMapGoogle — mapa real (Maps JavaScript API). Render imperativo: marcadores
// numerados por rota, polyline decodificada por subcluster, marcador da base e
// não-roteirizados em cinza. fitBounds só refaz quando o CONJUNTO de pontos
// muda — seleção/drag não recentralizam o mapa.
// ===========================================================================

// Converte um SVG inline em data-URL pronta pro icon.url do Marker.
function svgUrl(svg) {
   return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Fator de ampliação dos pinos de ITEM (rota + não-roteirizado). O SVG é
// vetorial, então só escalamos o tamanho de render (scaledSize/anchor) — a
// letra de cargo_class (A/B/C/D/E) cresce junto e fica mais legível. Não afeta
// o marcador da base (warehouse).
const PIN_SCALE = 1.3;

// Pino numerado da rota — espelha o HFPin do SVG (#3): círculo PREENCHIDO na
// cor da rota quando o item está PROCESSADO (done), ou FUNDO BRANCO com borda/
// número na cor quando ainda NÃO processado. Quadrado de volumetria (A/B/C/D)
// na borda inferior. Anel externo quando selecionado (#4).
function routePinIcon(g, { color, n, cargo, started, selected }) {
   const cx = 22;
   const cy = 24;
   const r = 13;
   const sq = 12;
   const sqY = cy + r;
   const badge = cargoBadge(cargo);
   // Iniciado (mesa de operação / terminal) → preenchido na cor da rota. Idle
   // puro (não iniciado) → fundo branco com borda + número na cor da rota.
   const circleFill = started ? color : '#FFFFFF';
   const numFill = started ? readableInk(color) : color;
   const ring = selected
      ? `<circle cx="${cx}" cy="${cy}" r="${r + 6}" fill="none" stroke="${color}" stroke-width="2.5" opacity="0.55"/>`
      : '';
   const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="60" viewBox="0 0 44 60">${ring}<circle cx="${cx}" cy="${cy}" r="${r}" fill="${circleFill}" stroke="${color}" stroke-width="2.5"/><text x="${cx}" y="${cy + 4}" text-anchor="middle" font-size="11" font-weight="700" font-family="monospace" fill="${numFill}">${n ?? ''}</text><rect x="${cx - sq / 2}" y="${sqY - sq / 2}" width="${sq}" height="${sq}" rx="2" fill="${badge.fill}" stroke="${color}" stroke-width="1.5"/><text x="${cx}" y="${sqY + 3}" text-anchor="middle" font-size="${badge.fontSize}" font-weight="800" font-family="monospace" fill="${badge.ink}">${badge.text}</text></svg>`;
   return {
      url: svgUrl(svg),
      scaledSize: new g.maps.Size(44 * PIN_SCALE, 60 * PIN_SCALE),
      anchor: new g.maps.Point(cx * PIN_SCALE, cy * PIN_SCALE),
   };
}

// Pino de item NÃO roteirizado (cinza, sem número de ordem). Fundo branco
// (item não processado, #3), com a letra de volumetria. Anel quando selecionado.
function unroutedPinIcon(g, { cargo, selected }) {
   const cx = 22;
   const cy = 24;
   const r = 11;
   const sq = 11;
   const sqY = cy + r;
   const gray = '#8B8B82';
   const badge = cargoBadge(cargo);
   const ring = selected
      ? `<circle cx="${cx}" cy="${cy}" r="${r + 6}" fill="none" stroke="#1F1F18" stroke-width="2.5" opacity="0.6"/>`
      : '';
   const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="58" viewBox="0 0 44 58">${ring}<circle cx="${cx}" cy="${cy}" r="${r}" fill="#FFFFFF" stroke="${gray}" stroke-width="2" stroke-dasharray="2 2"/><circle cx="${cx}" cy="${cy}" r="3" fill="${gray}"/><rect x="${cx - sq / 2}" y="${sqY - sq / 2}" width="${sq}" height="${sq}" rx="2" fill="${badge.fill}" stroke="${gray}" stroke-width="1.25"/><text x="${cx}" y="${sqY + 3}" text-anchor="middle" font-size="${badge.fontSize}" font-weight="800" font-family="monospace" fill="${badge.ink}">${badge.text}</text></svg>`;
   return {
      url: svgUrl(svg),
      scaledSize: new g.maps.Size(44 * PIN_SCALE, 58 * PIN_SCALE),
      anchor: new g.maps.Point(cx * PIN_SCALE, cy * PIN_SCALE),
   };
}

// Marcador da base (depósito) — ícone estilo warehouse (#1), badge escuro
// arredondado com galpão branco, pra o operador achar a base de relance.
function warehouseIcon(g) {
   const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect x="2" y="2" width="36" height="36" rx="9" fill="#1F1F18" stroke="#FFFFFF" stroke-width="2"/><path d="M9 18 L20 11 L31 18 L31 30 L9 30 Z" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/><rect x="17" y="22" width="6" height="8" fill="#FFFFFF"/><line x1="9" y1="22" x2="31" y2="22" stroke="#FFFFFF" stroke-width="1.5"/></svg>`;
   return {
      url: svgUrl(svg),
      scaledSize: new g.maps.Size(40, 40),
      anchor: new g.maps.Point(20, 20),
   };
}

// Conteúdo do balão ancorado no pino (InfoWindow). Montado em DOM imperativo —
// o InfoWindow não recebe React. Estrutura: header (nº do pedido + fechar),
// seção de ETAs (4 marcos; só pra item EM ROTA, quando `events` existe) e o
// botão que abre o card completo. `onClose`/`onCard` são callbacks do mapa.
// Linha "rótulo … valor" reaproveitada pelas estimativas e pelos marcos.
function balloonRow(className, label, value, pendingText) {
   const row = document.createElement('div');
   row.className = className;
   const l = document.createElement('span');
   l.className = 'lbl';
   l.textContent = label;
   const v = document.createElement('span');
   v.className = 'val';
   v.textContent = value || pendingText;
   row.append(l, v);
   return row;
}

function makeBalloonContent({ order, eta, etd, events, onClose, onCard }) {
   const root = document.createElement('div');
   root.className = 'hf-balloon';

   const head = document.createElement('div');
   head.className = 'hf-balloon-head';
   const ord = document.createElement('span');
   ord.className = 'hf-balloon-order';
   ord.textContent = order != null ? `#PEDIDO ${order}` : 'Pedido';
   const close = document.createElement('button');
   close.type = 'button';
   close.className = 'hf-balloon-close';
   close.setAttribute('aria-label', 'Fechar');
   close.textContent = '×';
   close.addEventListener('click', onClose);
   head.append(ord, close);
   root.appendChild(head);

   // Estimativas planejadas (chegada/saída) — só quando há ETA (item em rota).
   const etaVal = eta && eta !== '—' ? eta : null;
   if (etaVal || etd) {
      const est = document.createElement('div');
      est.className = 'hf-balloon-estimates';
      est.append(
         balloonRow('hf-balloon-est', 'Estimativa de chegada', etaVal, '—'),
         balloonRow('hf-balloon-est', 'Estimativa de saída', etd, '—')
      );
      root.appendChild(est);
   }

   // Marcos reais (eventos). Só pra item em rota; não-roteirizado não tem
   // cadeia de eventos → balão fica só com header + botão.
   if (events) {
      const timeline = document.createElement('div');
      timeline.className = 'hf-balloon-eta';
      const rows = [
         ['Trajeto iniciado', events.otw],
         ['Chegou no local', events.arrived],
         ['Iniciou o serviço', events.onService],
         [
            events.terminal ? events.terminal.label : 'Finalização',
            events.terminal ? events.terminal.t : null,
         ],
      ];
      rows.forEach(([label, value]) => {
         timeline.appendChild(
            balloonRow(
               `hf-balloon-row ${value ? 'is-done' : 'is-pending'}`,
               label,
               value,
               'Pendente'
            )
         );
      });
      root.appendChild(timeline);
   }

   const cardBtn = document.createElement('button');
   cardBtn.type = 'button';
   cardBtn.className = 'hf-balloon-card';
   cardBtn.textContent = 'Exibir card do pedido';
   cardBtn.addEventListener('click', onCard);
   root.appendChild(cardBtn);

   return root;
}

function HFMapGoogle({
   routes,
   unroutedGeo,
   base = null,
   onPinClick = null,
   onUnroutedPinClick = null,
   selectedItemId = null,
}) {
   const g = window.google;
   const containerRef = useRef(null);
   const mapRef = useRef(null);
   const infoWindowRef = useRef(null);
   const overlaysRef = useRef([]);
   const fitSigRef = useRef(null);
   const pannedToRef = useRef(null);

   // Init do mapa (uma vez).
   useEffect(() => {
      if (!containerRef.current || mapRef.current) return;
      mapRef.current = new g.maps.Map(containerRef.current, {
         center: base ? { lat: base.lat, lng: base.lng } : FALLBACK_CENTER,
         zoom: 11,
         mapTypeControl: false,
         streetViewControl: false,
         fullscreenControl: false,
         zoomControl: false, // ocultado a pedido — zoom só por scroll do mouse
         clickableIcons: false,
         keyboardShortcuts: false, // remove o link "Atalhos do teclado"
         isFractionalZoomEnabled: true, // permite zoom não-inteiro (passo suave)
         scrollwheel: false, // desliga o zoom nativo (passo fixo de 1 nível)
         gestureHandling: 'greedy', // sem overlay "use ctrl+scroll"
         styles: HIDE_POI_STYLES,
      });
      // Balão único reaproveitado por todos os pinos. headerDisabled: usamos
      // nosso próprio header (nº do pedido + fechar) no conteúdo.
      infoWindowRef.current = new g.maps.InfoWindow({ headerDisabled: true });
   }, [g, base]);

   // Zoom por scroll gradual e ancorado no cursor (substitui o passo nativo de
   // 1 nível). Listener próprio porque a API não expõe granularidade de scroll.
   useEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;

      const onWheel = (e) => {
         const map = mapRef.current;
         if (!map) return;
         e.preventDefault();

         const projection = map.getProjection();
         const bounds = map.getBounds();
         if (!projection || !bounds) return;

         const oldZoom = map.getZoom();
         let step = -e.deltaY * ZOOM_SENSITIVITY;
         step = Math.max(-MAX_ZOOM_STEP, Math.min(MAX_ZOOM_STEP, step));
         const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom + step));
         if (newZoom === oldZoom) return;

         // Mantém fixo o ponto do mapa que está sob o cursor (zoom ancorado):
         // converte o pixel do cursor → coord de mundo, e recalcula o centro
         // pro novo zoom de forma que esse ponto não se mexa na tela.
         const rect = container.getBoundingClientRect();
         const px = e.clientX - rect.left;
         const py = e.clientY - rect.top;

         const ne = projection.fromLatLngToPoint(bounds.getNorthEast());
         const sw = projection.fromLatLngToPoint(bounds.getSouthWest());
         const oldScale = 2 ** oldZoom;
         const topLeft = new g.maps.Point(sw.x, ne.y); // canto sup-esq do viewport
         const cursorWorld = new g.maps.Point(
            topLeft.x + px / oldScale,
            topLeft.y + py / oldScale
         );

         const newScale = 2 ** newZoom;
         const newTopLeft = new g.maps.Point(
            cursorWorld.x - px / newScale,
            cursorWorld.y - py / newScale
         );
         const newCenter = projection.fromPointToLatLng(
            new g.maps.Point(
               newTopLeft.x + rect.width / 2 / newScale,
               newTopLeft.y + rect.height / 2 / newScale
            )
         );

         map.setZoom(newZoom);
         map.setCenter(newCenter);
      };

      container.addEventListener('wheel', onWheel, { passive: false });
      return () => container.removeEventListener('wheel', onWheel);
   }, [g]);

   // Desenho dos overlays — refaz a cada mudança de rotas/itens/base/seleção.
   useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      // Limpa overlays anteriores. Fecha o balão: os marcadores são recriados,
      // então um balão aberto ficaria ancorado num marcador órfão.
      overlaysRef.current.forEach((o) => o.setMap(null));
      overlaysRef.current = [];
      infoWindowRef.current?.close();

      // Abre o balão ancorado num marcador, com o conteúdo montado na hora.
      const openBalloon = (marker, opts) => {
         const iw = infoWindowRef.current;
         if (!iw) return;
         iw.setContent(
            makeBalloonContent({ ...opts, onClose: () => iw.close() })
         );
         iw.open({ map, anchor: marker });
      };

      const bounds = new g.maps.LatLngBounds();
      let hasPoint = false;
      // Posição do item selecionado (roteirizado ou pendente) — pra dar panTo.
      let selectedPos = null;

      // Base (depósito) — ícone warehouse (#1).
      if (base) {
         const pos = { lat: base.lat, lng: base.lng };
         const marker = new g.maps.Marker({
            position: pos,
            map,
            title: `Base — ${base.label}`,
            zIndex: 9999,
            icon: warehouseIcon(g),
         });
         overlaysRef.current.push(marker);
         bounds.extend(pos);
         hasPoint = true;
      }

      // Rotas: polyline + marcadores numerados.
      routes.forEach((route) => {
         const { color, subclusterId, geo } = route;
         if (!geo) return;

         // Polyline: decodifica a do Google quando existe; senão liga os pontos
         // por reta. Esmaecida + tracejada quando a geometria está obsoleta.
         let path = null;
         if (geo.polyline && g.maps.geometry?.encoding) {
            path = g.maps.geometry.encoding.decodePath(geo.polyline);
         } else if (geo.points.length >= 2) {
            path = geo.points.map((p) => ({ lat: p.lat, lng: p.lng }));
         }
         if (path && path.length >= 2) {
            const line = new g.maps.Polyline({
               path,
               map,
               strokeColor: color,
               strokeOpacity: geo.stale ? 0 : 0.9,
               strokeWeight: geo.stale ? 3 : 4,
               icons: geo.stale
                  ? [
                       {
                          icon: {
                             path: 'M 0,-1 0,1',
                             strokeOpacity: 0.5,
                             strokeColor: color,
                             scale: 3,
                          },
                          offset: '0',
                          repeat: '12px',
                       },
                    ]
                  : undefined,
            });
            overlaysRef.current.push(line);
         }

         geo.points.forEach((p) => {
            const pos = { lat: p.lat, lng: p.lng };
            const isSelected =
               p.itemId != null && p.itemId === selectedItemId;
            if (isSelected) selectedPos = pos;
            const eta = p.eta && p.eta !== '—' ? ` · ETA ${p.eta}` : '';
            // Tooltip (#2): nº de ordem, número do PEDIDO, depois o cliente.
            const marker = new g.maps.Marker({
               position: pos,
               map,
               title: `${p.n}. ${p.id} · ${p.nm}${eta}`,
               icon: routePinIcon(g, {
                  color,
                  n: p.n,
                  cargo: p.cargo,
                  started: p.started,
                  selected: isSelected,
               }),
               zIndex: isSelected ? 9998 : undefined,
            });
            // Clique no pino → abre o balão. O drawer completo só abre pelo
            // botão "Exibir card do pedido" dentro do balão.
            marker.addListener('click', () =>
               openBalloon(marker, {
                  order: p.id,
                  eta: p.eta,
                  etd: p.etd,
                  events: p.events,
                  onCard: () => {
                     infoWindowRef.current?.close();
                     if (onPinClick && subclusterId) onPinClick(subclusterId, p.n);
                  },
               })
            );
            overlaysRef.current.push(marker);
            bounds.extend(pos);
            hasPoint = true;
         });
      });

      // Não-roteirizados (cinza, fundo branco = não processado).
      unroutedGeo.forEach((p) => {
         const pos = { lat: p.lat, lng: p.lng };
         const isSelected = p.itemId != null && p.itemId === selectedItemId;
         if (isSelected) selectedPos = pos;
         const title = p.id ? `${p.id} · ${p.nm}` : 'Não roteirizado';
         const marker = new g.maps.Marker({
            position: pos,
            map,
            title,
            icon: unroutedPinIcon(g, { cargo: p.cargo, selected: isSelected }),
            zIndex: isSelected ? 9998 : undefined,
         });
         // Não-roteirizado: balão só com header + botão (sem cadeia de eventos).
         marker.addListener('click', () =>
            openBalloon(marker, {
               order: p.id,
               events: null,
               onCard: () => {
                  infoWindowRef.current?.close();
                  if (onUnroutedPinClick && p.itemId != null) {
                     onUnroutedPinClick(p.itemId);
                  }
               },
            })
         );
         overlaysRef.current.push(marker);
         bounds.extend(pos);
         hasPoint = true;
      });

      // fitBounds só quando o conjunto de pontos muda (assinatura). Seleção não
      // recentraliza — evita "pulo" do mapa ao clicar num pino/card.
      const sig = JSON.stringify([
         base ? [base.lat, base.lng] : null,
         routes.map((r) => [r.subclusterId, r.geo?.points.map((p) => [p.lat, p.lng, p.n])]),
         unroutedGeo.map((p) => [p.lat, p.lng]),
      ]);
      if (hasPoint && sig !== fitSigRef.current) {
         fitSigRef.current = sig;
         map.fitBounds(bounds, 48);
         // Single point: fitBounds zooma demais — limita.
         const once = g.maps.event.addListenerOnce(map, 'idle', () => {
            if (map.getZoom() > 16) map.setZoom(16);
         });
         overlaysRef.current.push({ setMap: () => g.maps.event.removeListener(once) });
      }

      // Pan ao item selecionado (#4) — só quando a seleção MUDA. Centraliza no
      // item aberto no drawer (roteirizado ou pendente) sem mexer no zoom.
      if (selectedItemId == null) {
         pannedToRef.current = null;
      } else if (selectedPos && selectedItemId !== pannedToRef.current) {
         pannedToRef.current = selectedItemId;
         map.panTo(selectedPos);
      }
   }, [
      g,
      routes,
      unroutedGeo,
      base,
      selectedItemId,
      onPinClick,
      onUnroutedPinClick,
   ]);

   // Cleanup ao desmontar.
   useEffect(
      () => () => {
         overlaysRef.current.forEach((o) => o.setMap && o.setMap(null));
         overlaysRef.current = [];
         infoWindowRef.current?.close();
      },
      []
   );

   return <div ref={containerRef} className="hf-map" />;
}

HFMapGoogle.propTypes = {
   routes: PropTypes.array.isRequired,
   unroutedGeo: PropTypes.array.isRequired,
   base: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      label: PropTypes.string,
   }),
   onPinClick: PropTypes.func,
   onUnroutedPinClick: PropTypes.func,
   selectedItemId: PropTypes.number,
};

// ===========================================================================
// HFMap — ponto de troca SVG ↔ Google Maps (handoff 0030 §5.1). Com chave
// configurada E API carregada, renderiza o mapa real; enquanto a Maps JS API
// ainda baixa (carga fria), mostra um spinner; nos casos REAIS de fallback
// (sem chave, falha de carga, ou ?mock) cai no SVG esquemático.
// ===========================================================================
export default function HFMap({
   dark = false,
   routes,
   unroutedPins,
   unroutedGeo = [],
   base = null,
   onPinClick = null,
   onUnroutedPinClick = null,
   selected = null,
   selectedItemId = null,
   forceSvg = false,
}) {
   const { apiKey, isLoaded, loadError } = useGoogleMaps();
   const useReal = !forceSvg && apiKey && isLoaded && !loadError;
   // Carga fria da API: tem chave, sem erro, mas o script ainda não terminou.
   // Mostra spinner em vez do SVG — o mock esquemático fica reservado pros
   // casos reais de fallback (sem chave / loadError / ?mock). Com cache quente
   // o useGoogleMaps já inicia isLoaded=true, então o spinner nem pisca.
   const isLoadingMap = !forceSvg && apiKey && !isLoaded && !loadError;

   if (useReal) {
      return (
         <HFMapGoogle
            routes={routes}
            unroutedGeo={unroutedGeo}
            base={base}
            onPinClick={onPinClick}
            onUnroutedPinClick={onUnroutedPinClick}
            selectedItemId={selectedItemId}
         />
      );
   }

   if (isLoadingMap) {
      return (
         <div className="hf-map">
            <Loading isLoading />
         </div>
      );
   }

   return (
      <HFMapSVG
         dark={dark}
         routes={routes}
         unroutedPins={unroutedPins}
         onPinClick={onPinClick}
         selected={selected}
      />
   );
}

HFMap.propTypes = {
   dark: PropTypes.bool,
   routes: PropTypes.array.isRequired,
   unroutedPins: PropTypes.array.isRequired,
   unroutedGeo: PropTypes.array,
   base: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      label: PropTypes.string,
   }),
   onPinClick: PropTypes.func,
   onUnroutedPinClick: PropTypes.func,
   selected: PropTypes.shape({
      subclusterId: PropTypes.string,
      seq: PropTypes.number,
   }),
   selectedItemId: PropTypes.number,
   // Força o SVG mesmo com chave (usado no ?mock — sem dados geo reais).
   forceSvg: PropTypes.bool,
};
